import React, { useEffect, useRef } from "react"
import { Link as InertiaLink, router } from "@inertiajs/react"
import PropTypes from "prop-types"
// oxlint-disable-next-line rulesdir/no-old-button -- ButtonLink's migration is out of scope for this prop-type fix
import { OldButton } from "@kit/design/old-button"
import { canUseInertia } from "../navigation"

/**
 * Default prefetch time in inertia is 30 seconds. That might lead to unexpected behavior when data is cached for too long.
 * We only want to support the behavior where user hovers over a link and then navigates to it, 5 seconds seems to be enough for that.
 */
const DEFAULT_PREFETCH_CACHE_TIME = 5000

/**
 * Link component that conditionally renders either an Inertia Link or a regular anchor tag
 * based on the current route and feature flags.
 *
 * @param {React.ReactNode} children - Content to be rendered inside the link
 * @param {string} href - The URL the link points to
 * @param {boolean} [prefetch=false] - Whether to prefetch the link's destination
 * @param {Object} rest - Additional props to pass to the underlying link component
 * @returns {React.ReactElement} A Link component
 */
export const Link = React.forwardRef(function Link(
  { children, prefetch = false, onClick, ...props },
  ref
) {
  if (canUseInertia(props.href)) {
    // Clicks bubbling in from portaled children (e.g. menu popups) have a DOM
    // target outside the anchor. A native anchor wouldn't navigate on those,
    // so prevent Inertia's interception to keep the same semantics.
    const handleClick = event => {
      if (!event.currentTarget.contains(event.target)) {
        event.preventDefault()
      }
      onClick?.(event)
    }

    return (
      <InertiaLink
        ref={ref}
        prefetch={prefetch}
        cacheFor={DEFAULT_PREFETCH_CACHE_TIME}
        onClick={handleClick}
        {...props}
      >
        {children}
      </InertiaLink>
    )
  }

  const Component = props.as || "a"
  return (
    <Component ref={ref} onClick={onClick} {...props}>
      {children}
    </Component>
  )
})

Link.propTypes = {
  children: PropTypes.node,
  href: PropTypes.string,
  prefetch: PropTypes.bool,
  as: PropTypes.elementType
}

/**
 * @deprecated Consider using Link component with as={Button} prop
 */
export const ButtonLink = ({
  children,
  prefetch = false,
  onClick = () => {},
  ...props
}) => {
  const hoverTimeout = useRef(null)

  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeout.current)
    }
  }, [])

  const handleClick = event => {
    event.preventDefault()
    onClick(event)
    router.visit(props.href, {
      preserveScroll: props.preserveScroll,
      preserveState: props.preserveState
    })
  }

  const prefetchHoverEvents = {
    onMouseEnter: () => {
      hoverTimeout.current = window.setTimeout(() => {
        router.prefetch(
          props.href,
          { method: "get" },
          { cacheFor: DEFAULT_PREFETCH_CACHE_TIME }
        ) // The last object will be destructured with default values inside Inertia code
      }, 75)
    },
    onMouseLeave: () => {
      clearTimeout(hoverTimeout.current)
    }
  }

  if (canUseInertia(props.href)) {
    return (
      <OldButton
        onClick={handleClick}
        {...(prefetch ? prefetchHoverEvents : {})}
        {...props}
      >
        {children}
      </OldButton>
    )
  }

  return (
    <OldButton {...props} onClick={onClick}>
      {children}
    </OldButton>
  )
}
